import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { createContext, useContext, useState, useEffect } from "react";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ParticulierForm from "@/pages/ParticulierForm";
import ProfessionnelForm from "@/pages/ProfessionnelForm";
import EntrepriseForm from "@/pages/EntrepriseForm";
import CollectiviteForm from "@/pages/CollectiviteForm";
import CoproprieteForm from "@/pages/CoproprieteForm";
import ResultatEligibilite from "@/pages/ResultatEligibilite";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCRMIntegration from "@/pages/AdminCRMIntegration";
import AdminMarketPricesPage from "@/pages/AdminMarketPricesPage";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);

  // No useEffect needed - state is initialized from localStorage

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem("token", accessToken);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1A4D2E] border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-[#FAF9F6]">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            {/* Bénéficiaires */}
            <Route path="/particulier" element={<ParticulierForm />} />
            <Route path="/copropriete" element={<CoproprieteForm />} />
            <Route path="/entreprise" element={<EntrepriseForm />} />
            <Route path="/collectivite" element={<CollectiviteForm />} />
            {/* Artisans Partenaires */}
            <Route path="/professionnel" element={<ProfessionnelForm />} />
            <Route path="/partenaire" element={<ProfessionnelForm />} />
            {/* Résultats */}
            <Route path="/resultat/:leadId" element={<ResultatEligibilite />} />
            <Route
              path="/admin/crm"
              element={
                <ProtectedRoute adminOnly>
                  <AdminCRMIntegration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/market-prices"
              element={
                <ProtectedRoute adminOnly>
                  <AdminMarketPricesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
